import { useEffect, useRef, useState } from "react";
import { Check, X, Bell } from "lucide-react";
import { useData } from "@/context/DataContext";
import { isHiddenUser } from "@/lib/roles";
import { hasLinks, LinkTokens } from "@/components/LinkifiedText";

let __seq = 0;
export function newItem(text = "", done = false) {
  __seq += 1;
  return { id: `c_${Date.now()}_${__seq}`, text, note: "", done, notify_user_id: null };
}

export default function ChecklistEditor({ items, onChange, testIdPrefix = "checklist" }) {
  const inputsRef = useRef({});
  const [editingId, setEditingId] = useState(null);
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
  };
  const { users } = useData();
  const notifyableUsers = users.filter(
    (u) => !isHiddenUser(u) && u.role !== "cliente",
  );

  useEffect(() => {
    if (items.length === 0) {
      onChange([newItem()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };

  const addAfter = (idx) => {
    const nextItem = newItem();
    const next = [...items.slice(0, idx + 1), nextItem, ...items.slice(idx + 1)];
    onChange(next);
    setTimeout(() => {
      const el = inputsRef.current[nextItem.id];
      if (el) el.focus();
    }, 20);
  };

  const removeAt = (idx) => {
    if (items.length === 1) {
      onChange([newItem()]);
      return;
    }
    const next = items.filter((_, i) => i !== idx);
    onChange(next);
    setTimeout(() => {
      const prev = next[Math.max(0, idx - 1)];
      const el = prev && inputsRef.current[prev.id];
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 20);
  };

  const onKey = (e, idx, it) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addAfter(idx);
    } else if (e.key === "Backspace" && it.text === "") {
      e.preventDefault();
      removeAt(idx);
    }
  };

  const enterEdit = (it) => {
    setEditingId(it.id);
    setTimeout(() => {
      const el = inputsRef.current[it.id];
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 20);
  };

  return (
    <div className="space-y-1.5" data-testid={testIdPrefix}>
      {items.map((it, idx) => (
        <div
          key={it.id}
          data-testid={`${testIdPrefix}-row-${idx}`}
          className="group rounded-xl border border-white/10 bg-white/[0.03] px-2.5 py-2 hover:border-white/20 transition-colors"
        >
          <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setItem(idx, { done: !it.done })}
            data-testid={`${testIdPrefix}-toggle-${idx}`}
            aria-pressed={it.done ? "true" : "false"}
            className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-all border-2 ${
              it.done
                ? "bg-emerald-500 border-emerald-500 shadow-[0_0_10px_-2px_rgba(16,185,129,0.6)]"
                : "border-white/30 hover:border-white/70 bg-transparent"
            }`}
          >
            {it.done && <Check size={13} className="text-white" strokeWidth={3} />}
          </button>
          {hasLinks(it.text) && editingId !== it.id ? (
            <div
              data-testid={`${testIdPrefix}-linkview-${idx}`}
              onClick={() => enterEdit(it)}
              title="Clicca per modificare · i link sono cliccabili"
              className={`flex-1 min-w-0 text-sm cursor-text break-words whitespace-pre-wrap ${
                it.done ? "line-through text-zinc-500" : "text-zinc-100"
              }`}
            >
              <LinkTokens text={it.text} />
            </div>
          ) : (
            <textarea
              ref={(el) => {
                if (el) {
                  inputsRef.current[it.id] = el;
                  autoGrow(el);
                }
              }}
              rows={1}
              value={it.text}
              onChange={(e) => {
                setItem(idx, { text: e.target.value });
                autoGrow(e.target);
              }}
              onKeyDown={(e) => onKey(e, idx, it)}
              onFocus={() => setEditingId(it.id)}
              onBlur={() => setEditingId((cur) => (cur === it.id ? null : cur))}
              placeholder="Nuovo item…"
              data-testid={`${testIdPrefix}-input-${idx}`}
              className={`flex-1 min-w-0 bg-transparent border-0 outline-none text-sm font-medium resize-none overflow-hidden break-words whitespace-pre-wrap leading-snug py-0.5 ${
                it.done ? "line-through text-zinc-500" : "text-white"
              }`}
            />
          )}
          <select
            data-testid={`${testIdPrefix}-notify-${idx}`}
            value={it.notify_user_id || ""}
            onChange={(e) => setItem(idx, { notify_user_id: e.target.value || null })}
            title={it.notify_user_id ? "Notifica assegnata" : "Nessuna notifica"}
            className={`bg-transparent border-0 outline-none text-xs rounded-md px-1 py-1 mt-0.5 max-w-[120px] cursor-pointer transition-opacity ${
              it.notify_user_id ? "text-fuchsia-300" : "text-zinc-500 opacity-0 group-hover:opacity-100"
            }`}
          >
            <option value="" className="bg-zinc-900">
              — nessuno
            </option>
            {notifyableUsers.map((u) => (
              <option key={u.id} value={u.id} className="bg-zinc-900">
                Notifica {u.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => removeAt(idx)}
            data-testid={`${testIdPrefix}-remove-${idx}`}
            title="Rimuovi"
            className="opacity-0 group-hover:opacity-100 p-1 mt-0.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <X size={14} />
          </button>
          </div>
          <textarea
            ref={(el) => { if (el) autoGrow(el); }}
            rows={1}
            value={it.note || ""}
            onChange={(e) => {
              setItem(idx, { note: e.target.value });
              autoGrow(e.target);
            }}
            placeholder="Aggiungi informazioni…"
            data-testid={`${testIdPrefix}-note-${idx}`}
            className="mt-1 ml-9 w-[calc(100%-2.25rem)] bg-black/20 border border-white/10 rounded-md px-2.5 py-1.5 text-xs text-zinc-300 placeholder:text-zinc-600 outline-none focus:border-fuchsia-500/40 resize-none overflow-hidden break-words whitespace-pre-wrap leading-snug"
          />
        </div>
      ))}
    </div>
  );
}
